// NEC 2023 Table 250.122
// Minimum Size Equipment Grounding Conductors for Grounding Raceway and Equipment

import { ConductorMaterial, WireSize, COMPANY_STANDARDS } from '../types';

interface EGCEntry {
  maxOCPD: number;  // Maximum overcurrent device rating (Amps)
  copperSize: WireSize;  // Copper conductor size (internal representation)
  aluminumSize: WireSize;  // Aluminum conductor size (internal representation)
}

// Table entries - OCPD rating -> EGC size
const TABLE_250_122: EGCEntry[] = [
  { maxOCPD: 15, copperSize: -14, aluminumSize: -12 },
  { maxOCPD: 20, copperSize: -12, aluminumSize: -10 },
  { maxOCPD: 60, copperSize: -10, aluminumSize: -8 },
  { maxOCPD: 100, copperSize: -8, aluminumSize: -6 },
  { maxOCPD: 200, copperSize: -6, aluminumSize: -4 },
  { maxOCPD: 300, copperSize: -4, aluminumSize: -2 },
  { maxOCPD: 400, copperSize: -3, aluminumSize: -1 },
  { maxOCPD: 500, copperSize: -2, aluminumSize: 0 },
  { maxOCPD: 600, copperSize: -1, aluminumSize: 0.5 },
  { maxOCPD: 800, copperSize: 0, aluminumSize: 0.75 },
  { maxOCPD: 1000, copperSize: 0.5, aluminumSize: 0.9 },
  { maxOCPD: 1200, copperSize: 0.75, aluminumSize: 250 },
  { maxOCPD: 1600, copperSize: 0.9, aluminumSize: 350 },
  { maxOCPD: 2000, copperSize: 250, aluminumSize: 400 },
  { maxOCPD: 2500, copperSize: 350, aluminumSize: 600 },
  { maxOCPD: 3000, copperSize: 400, aluminumSize: 600 },
  { maxOCPD: 4000, copperSize: 500, aluminumSize: 750 },
  // Table 250.122 lists 1200 kcmil aluminum for 5000 A and 6000 A; 1200 kcmil
  // is not a size carried by this application, so the next listed size
  // (1250 kcmil) is used, which is conservative.
  { maxOCPD: 5000, copperSize: 700, aluminumSize: 1250 },
  { maxOCPD: 6000, copperSize: 800, aluminumSize: 1250 },
];

/**
 * Get minimum equipment grounding conductor size
 * @param ocpdRating - Overcurrent protective device rating in amps
 * @param material - Conductor material
 * @returns Wire size (internal representation)
 */
export function getEGCSize(
  ocpdRating: number,
  material: ConductorMaterial
): WireSize {
  // Find the entry with maxOCPD >= ocpdRating
  for (const entry of TABLE_250_122) {
    if (ocpdRating <= entry.maxOCPD) {
      const size = material === 'copper' ? entry.copperSize : entry.aluminumSize;
      // Apply company minimum (#12 for copper)
      if (material === 'copper' && size < COMPANY_STANDARDS.minConductorSize) {
        return COMPANY_STANDARDS.minConductorSize;
      }
      return size;
    }
  }

  // If OCPD is larger than any table value, use the largest
  const lastEntry = TABLE_250_122[TABLE_250_122.length - 1];
  return material === 'copper' ? lastEntry.copperSize : lastEntry.aluminumSize;
}

export const NEC_TABLE_250_122_REF = {
  table: 'Table 250.122',
  article: '250.122',
  description: 'Minimum Size Equipment Grounding Conductors for Grounding Raceway and Equipment',
};

export { TABLE_250_122 };
